import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Download, Upload, RotateCcw, Maximize2, Minimize2, BarChart3, GitBranch, Zap, CheckCircle2 } from 'lucide-react';

interface CalculationStats {
  calculationsPerformed: number;
  largestGCD: number;
  largestLCM: string;
  totalTime: number;
  averageTime: number;
  lastAlgorithm: string;
}

interface GCDLCMResult {
  numbers: number[];
  gcd: number;
  lcm: string;
  algorithm: string;
  processingTime: number;
  timestamp: Date;
  steps?: string[];
  properties?: string[];
  primeFactorizations?: { [key: number]: number[] };
  relationships?: string[];
}

const GCDLCMCalculatorTool: React.FC = () => {
  const [inputNumbers, setInputNumbers] = useState<string>('12, 18, 24');
  const [algorithm, setAlgorithm] = useState<'euclidean' | 'prime-factorization' | 'binary' | 'extended-euclidean'>('euclidean');
  const [showSteps, setShowSteps] = useState<boolean>(true);
  const [showProperties, setShowProperties] = useState<boolean>(false);
  const [showPrimeFactorization, setShowPrimeFactorization] = useState<boolean>(false);
  const [showRelationships, setShowRelationships] = useState<boolean>(false);
  const [result, setResult] = useState<GCDLCMResult | null>(null);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [stats, setStats] = useState<CalculationStats>({
    calculationsPerformed: 0,
    largestGCD: 0,
    largestLCM: '0',
    totalTime: 0,
    averageTime: 0,
    lastAlgorithm: ''
  });
  const [notification, setNotification] = useState<string>('');
  const [history, setHistory] = useState<GCDLCMResult[]>([]);

  const parseNumbers = (input: string): number[] => {
    return input
      .split(/[,\s]+/)
      .map(s => s.trim())
      .filter(s => s.length > 0)
      .map(s => parseInt(s))
      .filter(n => !isNaN(n) && n > 0);
  };

  const isPrime = (n: number): boolean => {
    if (n < 2) return false;
    if (n === 2) return true;
    if (n % 2 === 0) return false;
    
    for (let i = 3; i <= Math.sqrt(n); i += 2) {
      if (n % i === 0) return false;
    }
    return true;
  };

  const primeFactorization = (n: number): number[] => {
    const factors: number[] = [];
    let temp = n;
    
    for (let i = 2; i <= Math.sqrt(temp); i++) {
      while (temp % i === 0) {
        factors.push(i);
        temp = temp / i;
      }
    }
    
    if (temp > 1) {
      factors.push(temp);
    }
    
    return factors;
  };

  const euclideanGCD = (a: number, b: number, steps: string[] = []): { gcd: number; steps: string[] } => {
    const originalA = a;
    const originalB = b;
    
    if (steps.length === 0) {
      steps.push(`Computing GCD(${a}, ${b}) using Euclidean Algorithm:`);
    }
    
    while (b !== 0) {
      const quotient = Math.floor(a / b);
      const remainder = a % b;
      steps.push(`${a} = ${b} × ${quotient} + ${remainder}`);
      a = b;
      b = remainder;
    }
    
    steps.push(`GCD(${originalA}, ${originalB}) = ${a}`);
    return { gcd: a, steps };
  };

  const binaryGCD = (a: number, b: number, steps: string[] = []): { gcd: number; steps: string[] } => {
    if (steps.length === 0) {
      steps.push(`Computing GCD(${a}, ${b}) using Binary GCD Algorithm:`);
    }
    
    if (a === 0) {
      steps.push(`GCD(0, ${b}) = ${b}`);
      return { gcd: b, steps };
    }
    if (b === 0) {
      steps.push(`GCD(${a}, 0) = ${a}`);
      return { gcd: a, steps };
    }

    let shift = 0;
    while ((a | b) & 1) {
      a >>= 1;
      b >>= 1;
      shift++;
    }
    
    if (shift > 0) {
      steps.push(`Both numbers are even, factor out 2^${shift}`);
    }

    while ((a & 1) === 0) {
      a >>= 1;
    }

    do {
      while ((b & 1) === 0) {
        b >>= 1;
      }

      if (a > b) {
        [a, b] = [b, a];
      }

      b = b - a;
      steps.push(`Subtract: ${b + a} - ${a} = ${b}`);
    } while (b !== 0);

    const result = a << shift;
    steps.push(`Final result: ${a} × 2^${shift} = ${result}`);
    return { gcd: result, steps };
  };

  const extendedEuclideanGCD = (a: number, b: number, steps: string[] = []): { gcd: number; x: number; y: number; steps: string[] } => {
    if (steps.length === 0) {
      steps.push(`Computing GCD(${a}, ${b}) using Extended Euclidean Algorithm:`);
      steps.push(`Finding integers x, y such that ${a}x + ${b}y = GCD(${a}, ${b})`);
    }

    if (b === 0) {
      steps.push(`Base case: GCD(${a}, 0) = ${a}, x = 1, y = 0`);
      return { gcd: a, x: 1, y: 0, steps };
    }

    const result = extendedEuclideanGCD(b, a % b, steps);
    const x = result.y;
    const y = result.x - Math.floor(a / b) * result.y;

    steps.push(`Back-substitution: x = ${x}, y = ${y}`);
    steps.push(`Verification: ${a} × ${x} + ${b} × ${y} = ${a * x + b * y}`);

    return { gcd: result.gcd, x, y, steps };
  };

  const primeFactorizationGCD = (numbers: number[], steps: string[] = []): { gcd: number; steps: string[]; factorizations: { [key: number]: number[] } } => {
    steps.push(`Computing GCD using Prime Factorization:`);
    
    const factorizations: { [key: number]: number[] } = {};
    const factorCounts: { [key: number]: { [prime: number]: number } } = {};

    // Get prime factorization for each number
    for (const num of numbers) {
      const factors = primeFactorization(num);
      factorizations[num] = factors;
      factorCounts[num] = {};
      
      for (const factor of factors) {
        factorCounts[num][factor] = (factorCounts[num][factor] || 0) + 1;
      }
      
      steps.push(`${num} = ${factors.join(' × ')}`);
    }

    // Find common prime factors with minimum powers
    const allPrimes = new Set<number>();
    for (const counts of Object.values(factorCounts)) {
      for (const prime of Object.keys(counts)) {
        allPrimes.add(Number(prime));
      }
    }

    let gcd = 1;
    const gcdFactors: number[] = [];

    for (const prime of Array.from(allPrimes).sort((a, b) => a - b)) {
      let minCount = Infinity;
      for (const counts of Object.values(factorCounts)) {
        minCount = Math.min(minCount, counts[prime] || 0);
      }
      
      if (minCount > 0) {
        gcdFactors.push(...Array(minCount).fill(prime));
        gcd *= Math.pow(prime, minCount);
        steps.push(`Common factor ${prime}^${minCount} found in all numbers`);
      }
    }

    if (gcdFactors.length === 0) {
      steps.push(`No common prime factors found, GCD = 1`);
    } else {
      steps.push(`GCD = ${gcdFactors.join(' × ')} = ${gcd}`);
    }

    return { gcd, steps, factorizations };
  };

  const calculateGCD = (numbers: number[], algo: string): { gcd: number; steps: string[]; factorizations?: { [key: number]: number[] } } => {
    if (numbers.length === 0) return { gcd: 0, steps: [] };
    if (numbers.length === 1) return { gcd: numbers[0], steps: [`GCD of single number: ${numbers[0]}`] };

    let steps: string[] = [];
    let factorizations: { [key: number]: number[] } | undefined;

    if (algo === 'prime-factorization') {
      const result = primeFactorizationGCD(numbers, steps);
      return { gcd: result.gcd, steps: result.steps, factorizations: result.factorizations };
    }

    // For other algorithms, compute pairwise
    let currentGCD = numbers[0];
    steps.push(`Starting with first number: ${currentGCD}`);

    for (let i = 1; i < numbers.length; i++) {
      const nextNumber = numbers[i];
      steps.push(`\nComputing GCD(${currentGCD}, ${nextNumber}):`);

      let pairResult;
      switch (algo) {
        case 'euclidean':
          pairResult = euclideanGCD(currentGCD, nextNumber, []);
          break;
        case 'binary':
          pairResult = binaryGCD(currentGCD, nextNumber, []);
          break;
        case 'extended-euclidean':
          pairResult = extendedEuclideanGCD(currentGCD, nextNumber, []);
          break;
        default:
          pairResult = euclideanGCD(currentGCD, nextNumber, []);
      }

      steps.push(...pairResult.steps);
      currentGCD = pairResult.gcd;
    }

    return { gcd: currentGCD, steps };
  };

  const calculateLCM = (numbers: number[], gcd: number): bigint => {
    // LCM(a,b) = |a*b| / GCD(a,b)
    // For multiple numbers: LCM(a,b,c) = LCM(LCM(a,b), c)
    
    let lcm = BigInt(numbers[0]);
    const bigIntZero = BigInt(0);

    for (let i = 1; i < numbers.length; i++) {
      const nextNum = BigInt(numbers[i]);
      // Calculate GCD of current LCM and next number
      let a = lcm;
      let b = nextNum;
      
      while (b !== bigIntZero) {
        [a, b] = [b, a % b];
      }
      
      lcm = (lcm * nextNum) / a;
    }

    return lcm;
  };

  const getNumberProperties = (numbers: number[], gcd: number, lcm: string): string[] => {
    const properties: string[] = [];
    
    properties.push(`Analysis of numbers: ${numbers.join(', ')}`);
    properties.push(`• Count: ${numbers.length} numbers`);
    properties.push(`• Range: ${Math.min(...numbers)} to ${Math.max(...numbers)}`);
    properties.push(`• Sum: ${numbers.reduce((a, b) => a + b, 0)}`);
    properties.push(`• Average: ${(numbers.reduce((a, b) => a + b, 0) / numbers.length).toFixed(2)}`);
    
    properties.push(`\nGCD Properties:`);
    properties.push(`• GCD(${numbers.join(', ')}) = ${gcd}`);
    
    if (gcd === 1) {
      properties.push(`• Numbers are coprime (relatively prime)`);
    } else {
      properties.push(`• Numbers share common factor(s)`);
      const quotients = numbers.map(n => n / gcd);
      properties.push(`• Quotients when divided by GCD: ${quotients.join(', ')}`);
      
      // Check if quotients are coprime
      if (quotients.length === 2) {
        const quotientGCD = euclideanGCD(quotients[0], quotients[1]).gcd;
        if (quotientGCD === 1) {
          properties.push(`• Quotients are coprime`);
        }
      }
    }
    
    properties.push(`\nLCM Properties:`);
    properties.push(`• LCM(${numbers.join(', ')}) = ${lcm}`);
    
    // Check relationship between GCD and LCM
    if (numbers.length === 2) {
      const product = BigInt(numbers[0]) * BigInt(numbers[1]);
      const gcdLcmProduct = BigInt(gcd) * BigInt(lcm);
      properties.push(`• GCD × LCM = ${gcd} × ${lcm} = ${gcdLcmProduct}`);
      properties.push(`• Product of numbers = ${numbers[0]} × ${numbers[1]} = ${product}`);
      if (product === gcdLcmProduct) {
        properties.push(`• Fundamental theorem verified: GCD(a,b) × LCM(a,b) = a × b`);
      }
    }
    
    // Check for special relationships
    const maxNum = Math.max(...numbers);
    if (BigInt(lcm) === BigInt(maxNum)) {
      properties.push(`• LCM equals the largest number (one number divides all others)`);
    }
    
    return properties;
  };

  const getRelationships = (numbers: number[], gcd: number): string[] => {
    const relationships: string[] = [];
    
    relationships.push(`Mathematical Relationships:`);
    
    // Divisibility relationships
    for (let i = 0; i < numbers.length; i++) {
      for (let j = i + 1; j < numbers.length; j++) {
        const a = numbers[i];
        const b = numbers[j];
        
        if (a % b === 0) {
          relationships.push(`• ${a} is divisible by ${b} (${a} = ${a/b} × ${b})`);
        } else if (b % a === 0) {
          relationships.push(`• ${b} is divisible by ${a} (${b} = ${b/a} × ${a})`);
        } else {
          const pairGCD = euclideanGCD(a, b).gcd;
          if (pairGCD === 1) {
            relationships.push(`• ${a} and ${b} are coprime`);
          } else {
            relationships.push(`• GCD(${a}, ${b}) = ${pairGCD}`);
          }
        }
      }
    }
    
    // Factor relationships
    relationships.push(`\nFactor Analysis:`);
    for (const num of numbers) {
      const factors = [];
      for (let i = 1; i <= Math.sqrt(num); i++) {
        if (num % i === 0) {
          factors.push(i);
          if (i !== num / i) {
            factors.push(num / i);
          }
        }
      }
      factors.sort((a, b) => a - b);
      relationships.push(`• Factors of ${num}: ${factors.join(', ')}`);
    }
    
    return relationships;
  };

  const calculateGCDLCM = useCallback((): GCDLCMResult | null => {
    const startTime = performance.now();
    const numbers = parseNumbers(inputNumbers);
    
    if (numbers.length === 0 || numbers.some(n => n <= 0 || n > 1000000)) {
      return null;
    }

    try {
      const gcdResult = calculateGCD(numbers, algorithm);
      const lcm = calculateLCM(numbers, gcdResult.gcd);
      
      let properties: string[] | undefined;
      let relationships: string[] | undefined;
      
      if (showProperties) {
        properties = getNumberProperties(numbers, gcdResult.gcd, lcm.toString());
      }
      
      if (showRelationships) {
        relationships = getRelationships(numbers, gcdResult.gcd);
      }

      const endTime = performance.now();
      
      return {
        numbers,
        gcd: gcdResult.gcd,
        lcm: lcm.toString(),
        algorithm,
        processingTime: endTime - startTime,
        timestamp: new Date(),
        steps: showSteps ? gcdResult.steps : undefined,
        properties,
        primeFactorizations: showPrimeFactorization ? gcdResult.factorizations : undefined,
        relationships
      };
    } catch (error) {
      return null;
    }
  }, [inputNumbers, algorithm, showSteps, showProperties, showPrimeFactorization, showRelationships]);

  const performCalculation = useCallback((): void => {
    const calculationResult = calculateGCDLCM();
    if (calculationResult) {
      setResult(calculationResult);
      setHistory(prev => [calculationResult, ...prev.slice(0, 9)]); // Keep last 10 calculations
      
      setStats(prev => ({
        calculationsPerformed: prev.calculationsPerformed + 1,
        largestGCD: Math.max(prev.largestGCD, calculationResult.gcd),
        largestLCM: BigInt(calculationResult.lcm) > BigInt(prev.largestLCM) ? calculationResult.lcm : prev.largestLCM,
        totalTime: prev.totalTime + calculationResult.processingTime,
        averageTime: (prev.totalTime + calculationResult.processingTime) / (prev.calculationsPerformed + 1),
        lastAlgorithm: calculationResult.algorithm
      }));
    } else {
      setResult(null);
    }
  }, [calculateGCDLCM]);

  useEffect(() => {
    if (inputNumbers.trim()) {
      performCalculation();
    }
  }, [inputNumbers, algorithm, showSteps, showProperties, showPrimeFactorization, showRelationships, performCalculation]);

  const showNotification = (message: string): void => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  };

  const copyToClipboard = async (): Promise<void> => {
    if (!result) return;
    
    let textToCopy = `GCD/LCM Calculation:\n`;
    textToCopy += `Numbers: ${result.numbers.join(', ')}\n`;
    textToCopy += `GCD: ${result.gcd}\n`;
    textToCopy += `LCM: ${result.lcm}\n`;
    textToCopy += `Algorithm: ${result.algorithm}\n\n`;
    
    if (result.steps) {
      textToCopy += 'Steps:\n' + result.steps.join('\n') + '\n\n';
    }
    
    if (result.properties) {
      textToCopy += 'Properties:\n' + result.properties.join('\n') + '\n\n';
    }
    
    if (result.relationships) {
      textToCopy += 'Relationships:\n' + result.relationships.join('\n');
    }
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      showNotification('Result copied to clipboard!');
    } catch (err) {
      showNotification('Failed to copy');
    }
  };

  const downloadResult = (): void => {
    if (!result) return;
    
    let content = `GCD/LCM Calculation Report\n`;
    content += `Generated: ${result.timestamp.toLocaleString()}\n`;
    content += `Algorithm: ${result.algorithm}\n`;
    content += `Numbers: ${result.numbers.join(', ')}\n`;
    content += `GCD: ${result.gcd}\n`;
    content += `LCM: ${result.lcm}\n`;
    content += `Processing Time: ${result.processingTime.toFixed(2)}ms\n\n`;
    
    if (result.steps) {
      content += `Calculation Steps:\n${result.steps.join('\n')}\n\n`;
    }
    
    if (result.primeFactorizations) {
      content += `Prime Factorizations:\n`;
      for (const [num, factors] of Object.entries(result.primeFactorizations)) {
        content += `${num} = ${factors.join(' × ')}\n`;
      }
      content += '\n';
    }
    
    if (result.properties) {
      content += `Properties:\n${result.properties.join('\n')}\n\n`;
    }
    
    if (result.relationships) {
      content += `Relationships:\n${result.relationships.join('\n')}\n\n`;
    }
    
    content += `Statistics:\n`;
    content += `- Total Calculations: ${stats.calculationsPerformed}\n`;
    content += `- Largest GCD: ${stats.largestGCD}\n`;
    content += `- Largest LCM: ${stats.largestLCM}\n`;
    content += `- Average Processing Time: ${stats.averageTime.toFixed(2)}ms\n`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'gcd-lcm-calculation.txt';
    a.click();
    URL.revokeObjectURL(url);
    showNotification('Report downloaded!');
  };

  const clearAll = (): void => {
    setInputNumbers('12, 18, 24');
    setAlgorithm('euclidean');
    setShowSteps(true);
    setShowProperties(false);
    setShowPrimeFactorization(false);
    setShowRelationships(false);
    setResult(null);
    setHistory([]);
    setStats({
      calculationsPerformed: 0,
      largestGCD: 0,
      largestLCM: '0',
      totalTime: 0,
      averageTime: 0,
      lastAlgorithm: ''
    });
    showNotification('Calculator reset!');
  };

  const toggleFullscreen = (): void => {
    setIsFullscreen(!isFullscreen);
  };

  const loadSampleCalculation = (): void => {
    setInputNumbers('48, 18, 24');
    setAlgorithm('euclidean');
    setShowSteps(true);
    setShowProperties(true);
    setShowPrimeFactorization(true);
    setShowRelationships(true);
    showNotification('Sample calculation loaded!');
  };

  const getAlgorithmDescription = (algo: string): string => {
    switch (algo) {
      case 'euclidean': return 'Classical Euclidean Algorithm using division';
      case 'prime-factorization': return 'Prime factorization method';
      case 'binary': return 'Binary GCD using bitwise operations';
      case 'extended-euclidean': return 'Extended Euclidean with Bézout coefficients';
      default: return '';
    }
  };

  const containerClass = isFullscreen 
    ? "fixed inset-0 z-50 bg-gray-900" 
    : "min-h-screen bg-gradient-to-br from-gray-900 via-green-900 to-teal-900";

  return (
    <div className={containerClass}>
      <div className="container mx-auto p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-green-500 to-teal-600 rounded-lg">
              <GitBranch className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">GCD/LCM Calculator</h1>
              <p className="text-gray-300 text-sm">Advanced calculator for Greatest Common Divisor and Least Common Multiple</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Algorithm Selector */}
        <div className="mb-6 bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Algorithm Selection</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { value: 'euclidean', label: 'Euclidean', color: 'bg-green-600' },
              { value: 'prime-factorization', label: 'Prime Factorization', color: 'bg-blue-600' },
              { value: 'binary', label: 'Binary GCD', color: 'bg-purple-600' },
              { value: 'extended-euclidean', label: 'Extended Euclidean', color: 'bg-teal-600' }
            ].map((algo) => (
              <button
                key={algo.value}
                onClick={() => setAlgorithm(algo.value as any)}
                className={`p-3 rounded-lg border-2 transition-colors ${
                  algorithm === algo.value
                    ? `${algo.color} border-white text-white`
                    : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
                }`}
              >
                <div className="font-medium">{algo.label}</div>
                <div className="text-xs opacity-75 mt-1">
                  {getAlgorithmDescription(algo.value)}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Calculation Analytics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-green-400">{stats.calculationsPerformed}</div>
                <div className="text-gray-300 text-sm">Calculations</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-teal-400">{stats.averageTime.toFixed(2)}ms</div>
                <div className="text-gray-300 text-sm">Avg Time</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-blue-400">{stats.largestGCD}</div>
                <div className="text-gray-300 text-sm">Largest GCD</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-xl font-bold text-purple-400">
                  {stats.largestLCM.length > 8 ? 
                    `${stats.largestLCM.length} digits` : 
                    stats.largestLCM
                  }
                </div>
                <div className="text-gray-300 text-sm">Largest LCM</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-lg font-bold text-yellow-400">{stats.lastAlgorithm.replace('-', ' ') || 'None'}</div>
                <div className="text-gray-300 text-sm">Last Algorithm</div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6 min-h-[600px]">
          {/* Input Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Input & Options</h2>
              <div className="flex space-x-2">
                <button
                  onClick={loadSampleCalculation}
                  className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <Zap className="w-4 h-4" />
                </button>
                <button
                  onClick={clearAll}
                  className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="space-y-4 flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Numbers (comma or space separated)
                </label>
                <textarea
                  value={inputNumbers}
                  onChange={(e) => setInputNumbers(e.target.value)}
                  placeholder="Enter numbers (e.g., 12, 18, 24)"
                  className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none resize-none h-20"
                />
                <div className="text-xs text-gray-400 mt-1">
                  Enter 2 or more positive integers (max: 1,000,000)
                </div>
              </div>

              <div className="border-t border-gray-600 pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300">
                    Show Algorithm Steps
                  </label>
                  <button
                    onClick={() => setShowSteps(!showSteps)}
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                      showSteps ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'
                    }`}
                  >
                    {showSteps ? 'ON' : 'OFF'}
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300">
                    Mathematical Properties
                  </label>
                  <button
                    onClick={() => setShowProperties(!showProperties)}
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                      showProperties ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'
                    }`}
                  >
                    {showProperties ? 'ON' : 'OFF'}
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300">
                    Prime Factorizations
                  </label>
                  <button
                    onClick={() => setShowPrimeFactorization(!showPrimeFactorization)}
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                      showPrimeFactorization ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'
                    }`}
                  >
                    {showPrimeFactorization ? 'ON' : 'OFF'}
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300">
                    Number Relationships
                  </label>
                  <button
                    onClick={() => setShowRelationships(!showRelationships)}
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                      showRelationships ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'
                    }`}
                  >
                    {showRelationships ? 'ON' : 'OFF'}
                  </button>
                </div>
              </div>

              {/* Quick Examples */}
              <div className="border-t border-gray-600 pt-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Quick Examples
                </label>
                <div className="grid grid-cols-1 gap-2">
                  {[
                    { numbers: '12, 18, 24', label: 'Basic Example' },
                    { numbers: '48, 18', label: 'Two Numbers' },
                    { numbers: '15, 25, 35', label: 'Multiple of 5' },
                    { numbers: '17, 19, 23', label: 'Prime Numbers' },
                    { numbers: '100, 150, 200', label: 'Large Numbers' }
                  ].map((example) => (
                    <button
                      key={example.numbers}
                      onClick={() => setInputNumbers(example.numbers)}
                      className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded text-sm transition-colors text-left"
                    >
                      <div className="font-medium">{example.label}</div>
                      <div className="text-xs text-gray-400">{example.numbers}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Output Section */}
          <div className="lg:col-span-2 bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Results</h2>
              <div className="flex space-x-2">
                <button
                  onClick={copyToClipboard}
                  disabled={!result}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={downloadResult}
                  disabled={!result}
                  className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 bg-gray-900 rounded-lg p-4 overflow-y-auto min-h-[500px]">
              {result ? (
                <div className="space-y-4">
                  <div className="text-center border-b border-gray-600 pb-4">
                    <div className="text-sm text-gray-400 mb-2">
                      Numbers: {result.numbers.join(', ')} | Algorithm: {result.algorithm}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-700 p-3 rounded">
                        <div className="text-2xl font-bold text-green-400">GCD = {result.gcd}</div>
                        <div className="text-gray-300 text-sm">Greatest Common Divisor</div>
                      </div>
                      <div className="bg-gray-700 p-3 rounded">
                        <div className="text-2xl font-bold text-teal-400 break-all">
                          LCM = {result.lcm.length > 20 ? `${result.lcm.substring(0, 20)}...` : result.lcm}
                        </div>
                        <div className="text-gray-300 text-sm">Least Common Multiple</div>
                      </div>
                    </div>
                    <div className="text-gray-400 text-sm mt-2">
                      Calculated in {result.processingTime.toFixed(2)}ms | {result.timestamp.toLocaleString()}
                    </div>
                  </div>

                  {result.primeFactorizations && (
                    <div>
                      <h4 className="text-white font-medium mb-2">Prime Factorizations:</h4>
                      <div className="bg-gray-700 p-3 rounded text-sm space-y-1">
                        {Object.entries(result.primeFactorizations).map(([num, factors]) => (
                          <div key={num} className="text-blue-400 font-mono">
                            {num} = {factors.join(' × ')}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {result.steps && result.steps.length > 0 && (
                    <div>
                      <h4 className="text-white font-medium mb-2">Algorithm Steps:</h4>
                      <div className="bg-gray-700 p-3 rounded text-sm max-h-48 overflow-y-auto">
                        {result.steps.map((step, index) => (
                          <div key={index} className={`${step.startsWith('Computing') || step.startsWith('Finding') ? 'text-yellow-400 font-medium' : 'text-green-400 font-mono'} mb-1`}>
                            {step}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {result.properties && result.properties.length > 0 && (
                    <div>
                      <h4 className="text-white font-medium mb-2">Mathematical Properties:</h4>
                      <div className="bg-gray-700 p-3 rounded text-sm space-y-1 max-h-48 overflow-y-auto">
                        {result.properties.map((prop, index) => (
                          <div key={index} className={`${prop.startsWith('•') ? 'text-purple-400 ml-4' : 'text-white font-medium'}`}>
                            {prop}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {result.relationships && result.relationships.length > 0 && (
                    <div>
                      <h4 className="text-white font-medium mb-2">Number Relationships:</h4>
                      <div className="bg-gray-700 p-3 rounded text-sm space-y-1 max-h-48 overflow-y-auto">
                        {result.relationships.map((rel, index) => (
                          <div key={index} className={`${rel.startsWith('•') ? 'text-orange-400 ml-4' : 'text-white font-medium'}`}>
                            {rel}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {result.lcm.length > 20 && (
                    <div>
                      <h4 className="text-white font-medium mb-2">Complete LCM:</h4>
                      <div className="bg-gray-700 p-3 rounded font-mono text-sm text-teal-400 max-h-32 overflow-y-auto break-all">
                        {result.lcm}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        {result.lcm.length} digits
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <div className="text-center">
                    <GitBranch className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Enter numbers to calculate GCD and LCM</p>
                    <p className="text-sm mt-2">Separate numbers with commas or spaces</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* History Section */}
        {history.length > 0 && (
          <div className="mt-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Recent Calculations</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-40 overflow-y-auto">
              {history.slice(0, 6).map((calc, index) => (
                <div key={index} className="bg-gray-700 p-3 rounded">
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm font-medium text-white">
                      {calc.numbers.join(', ')}
                    </div>
                    <div className="text-xs text-gray-400">
                      {calc.processingTime.toFixed(1)}ms
                    </div>
                  </div>
                  <div className="text-xs space-y-1">
                    <div className="text-green-400">GCD: {calc.gcd}</div>
                    <div className="text-teal-400 truncate">
                      LCM: {calc.lcm.length > 15 ? `${calc.lcm.substring(0, 15)}...` : calc.lcm}
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {calc.algorithm} | {calc.timestamp.toLocaleTimeString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mathematical Information */}
        <div className="mt-6 bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">About GCD & LCM</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-gray-700 p-3 rounded">
              <h4 className="font-medium text-green-400 mb-2">Greatest Common Divisor</h4>
              <p className="text-gray-300">
                GCD(a,b) is the largest positive integer that divides both a and b.<br/>
                Also known as Greatest Common Factor (GCF).<br/>
                GCD(a,b) × LCM(a,b) = a × b (for two numbers)<br/>
                Used in fraction simplification and cryptography.
              </p>
            </div>
            <div className="bg-gray-700 p-3 rounded">
              <h4 className="font-medium text-teal-400 mb-2">Least Common Multiple</h4>
              <p className="text-gray-300">
                LCM(a,b) is the smallest positive integer divisible by both a and b.<br/>
                Used for adding/subtracting fractions.<br/>
                LCM(a,b) = |a×b| / GCD(a,b)<br/>
                For multiple numbers: LCM(a,b,c) = LCM(LCM(a,b), c)
              </p>
            </div>
            <div className="bg-gray-700 p-3 rounded">
              <h4 className="font-medium text-blue-400 mb-2">Algorithms</h4>
              <p className="text-gray-300">
                Euclidean: Classical division-based method<br/>
                Binary GCD: Uses bitwise operations<br/>
                Prime Factorization: Uses prime factors<br/>
                Extended: Finds Bézout coefficients
              </p>
            </div>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50">
            <CheckCircle2 className="w-4 h-4" />
            <span>{notification}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default GCDLCMCalculatorTool;